import { BookingV4, Item, Preorder } from "../../../../../types/bookings";
import { orderBy } from "../../../../../utils/OrderBy";
import PreOrderItem from "./CustomColumns/PreOrderItem";
import PreOrderPrice from "./CustomColumns/PreOrderPrice";
import PreOrderTime from "./CustomColumns/PreOrderTime";

type PreOrdersDataType = {
  // Time
  timeCol: JSX.Element;

  // Item
  itemCol: JSX.Element;

  quantity: number;
  priceCol: JSX.Element;
  total: string;
};
export const getPreOrdersData = (
  selectedBookingV4: BookingV4 | undefined,
  currency: string
): PreOrdersDataType[] => {
  if (!selectedBookingV4) {
    return [];
  }

  const preOrders = getActivePreOrders(selectedBookingV4);
  const preOrderItems = getMenuPreOrderItems(preOrders);
  const groupedPreOrderItems = sortPreOrderItemsBySubType(preOrderItems);

  return mapDataForTable(groupedPreOrderItems, currency);
};

const getMenuPreOrderItems = (preOrders: Preorder[]): Item[] => {
  const preOrderItems: Item[] = [];
  preOrders.forEach((row) => {
    if (Array.isArray(row.items)) {
      preOrderItems.push(...row.items);
    }
  });

  return preOrderItems;
};

type groupedItem = {
  key: string;
  items: Item[];
};

const sortPreOrderItemsBySubType = (preOrderItems: Item[]): Item[] => {
  let preOrderItemsBySubType: groupedItem[] = [];
  preOrderItems.forEach((item) => {
    // create group if doesn't exist
    if (
      !preOrderItemsBySubType.find(
        (itemBySubType) => itemBySubType.key === item.sub_type
      )
    ) {
      const newItemGroup = {
        key: item.sub_type ?? "",
        items: [item]
      };

      // if group has no subtype add to start
      if (!item.sub_type || item.sub_type === "") {
        preOrderItemsBySubType.unshift(newItemGroup);
        return;
      }

      preOrderItemsBySubType.push(newItemGroup);
      return;
    }

    // if group exists merge preOrder Items
    preOrderItemsBySubType = preOrderItemsBySubType.map((itemBySubType) => {
      if (itemBySubType.key === (item.sub_type ?? "")) {
        return {
          ...itemBySubType,
          items: itemBySubType.items.concat([item])
        };
      }

      return itemBySubType;
    });
  });

  // sort each grouped subtype item by time
  const items = preOrderItemsBySubType.reduce((item, groupedItem) => {
    return item.concat(
      orderBy({ data: groupedItem.items, field: "time", asc: true })
    );
  }, [] as Item[]);

  return items;
};

export const mapDataForTable = (
  preOrderItems: Item[],
  currency: string
): PreOrdersDataType[] => {
  let heading = "";
  return preOrderItems.map((item) => {
    // Show grouped subtype heading once
    let subType = getSubType(item.sub_type);
    if (heading !== getSubType(item.sub_type)) {
      heading = getSubType(item.sub_type);
    } else {
      subType = "";
    }

    const options = getItemOptionsString(item);
    const itemName = `${item.name}${options ? ` - ${options}` : ""}`;

    const itemCustomer =
      (item.for_name ? `For: ${item.for_name}` : "") +
      (item.for_name && item.seat ? " - " : "") +
      (item.seat ? `Seat: ${item.seat}` : "");

    return {
      timeCol: PreOrderTime({
        subType,
        time: item.time
      }),
      itemCol: PreOrderItem({
        name: itemName,
        description: item.description,
        customer: itemCustomer
      }),
      name: item.name,
      description: item.description,
      for_name: item.for_name,
      quantity: item.quantity,
      priceCol: PreOrderPrice({
        price: toCurrency(item.price, currency),
        discount: getItemDiscount(item, currency)
      }),
      total: toCurrency(item.total, currency),
      type: item.type,
      sub_type: item.sub_type,
      time: item.time
    };
  });
};

type PreOrdersTotalType = {
  description: string;
  total: string;
};

export type PreOrderTotalsRaw = {
  discounts: number;
  ordersTotal: number;
  serviceCharge: number;
  grandTotal: number;
  totalPaid: number;
  remainingToPay: number;
  minSpend: number;
  remainingSpend: number;
  hasMinSpend: boolean;
};

export const getPreOrderTotalsRaw = (
  selectedBookingV4: BookingV4 | undefined
): PreOrderTotalsRaw => {
  if (!selectedBookingV4) {
    return {
      discounts: 0,
      ordersTotal: 0,
      serviceCharge: 0,
      grandTotal: 0,
      totalPaid: 0,
      remainingToPay: 0,
      minSpend: 0,
      remainingSpend: 0,
      hasMinSpend: false
    };
  }

  const discounts = getPreOrderTotalOrderDiscounts(selectedBookingV4);
  const ordersTotal = getPreOrderTotalCost(selectedBookingV4);
  const serviceCharge = getBookingServiceChargeTotal(selectedBookingV4);
  const grandTotal = ordersTotal + serviceCharge;

  const { ordersPaidTotal, depositsTotal } =
    calculateTotalPaid(selectedBookingV4);
  const totalPaid = ordersPaidTotal + depositsTotal;

  const remainingToPay = grandTotal - totalPaid;
  const minSpend = getBookingMinSpend(selectedBookingV4);
  const remainingSpend = getBookingRemainingSpend(selectedBookingV4);
  const hasMinSpend = selectedBookingV4?.min_spend?.amount !== undefined;

  return {
    discounts,
    ordersTotal,
    serviceCharge,
    grandTotal,
    totalPaid,
    remainingToPay,
    minSpend,
    remainingSpend,
    hasMinSpend
  };
};

export const getPreOrderTotals = (
  selectedBookingV4: BookingV4 | undefined,
  currency: string
): PreOrdersTotalType[] => {
  if (!selectedBookingV4) {
    return [];
  }

  const raw = getPreOrderTotalsRaw(selectedBookingV4);

  const preOrderTotals: PreOrdersTotalType[] = [];
  if (raw.discounts > 0) {
    preOrderTotals.push({
      description: "Discounts",
      total: toCurrency(raw.discounts, currency)
    });
  }

  preOrderTotals.push({
    description: "Orders total  ",
    total: toCurrency(raw.ordersTotal, currency)
  });
  preOrderTotals.push({
    description: "Service charge",
    total: toCurrency(raw.serviceCharge, currency)
  });
  preOrderTotals.push({
    description: "Orders grand total",
    total: toCurrency(raw.grandTotal, currency)
  });
  preOrderTotals.push({
    description: "Total paid",
    total: toCurrency(raw.totalPaid, currency)
  });
  if (raw.hasMinSpend) {
    preOrderTotals.push({
      description: "Minimum spend",
      total: toCurrency(raw.minSpend, currency)
    });
    preOrderTotals.push({
      description: "Remaining to spend",
      total: toCurrency(raw.remainingSpend, currency)
    });
  }

  preOrderTotals.push({
    description: "Balance due",
    total: toCurrency(raw.remainingToPay, currency)
  });

  return preOrderTotals;
};

const getItemDiscount = (item: Item, currency: string): string => {
  let discount = "";
  if (item.discount_amount) {
    if (item.discount_type === "percentage") {
      discount += `(inc ${item.discount_amount}% discount)`;
    } else {
      discount += `(inc ${toCurrency(
        item.discount_amount,
        currency
      )} discount)`;
    }
  }
  return discount;
};

const getItemOptionsString = (item: Item): string => {
  let optionsStringParts: string[] = [];

  item.options?.forEach((option) => {
    const values = option.values.map((innerOption) => innerOption.label);

    if (values) {
      optionsStringParts = optionsStringParts.concat(values);
    }
  });

  return optionsStringParts.join(", ");
};

const getPreOrderTotalOrderDiscounts = (booking: BookingV4): number => {
  let totalDiscount = 0;

  if (Array.isArray(booking.preorders)) {
    totalDiscount = getActivePreOrders(booking).reduce((total, order) => {
      total += order.total_discount ?? 0;

      return total;
    }, 0);
  }

  return totalDiscount;
};

const getActivePreOrders = (booking: BookingV4) =>
  booking.preorders?.filter(
    (preOrder) =>
      !preOrder.status || (preOrder.status && preOrder.status !== "deleted")
  ) ?? [];

const getPreOrderTotalCost = (booking: BookingV4): number => {
  let preOrdersTotal = 0;

  if (Array.isArray(booking.preorders)) {
    preOrdersTotal = getActivePreOrders(booking).reduce((total, order) => {
      total += order.total_cost ?? 0;

      return total;
    }, 0);
  }

  return preOrdersTotal;
};

const getBookingServiceChargeTotal = (booking: BookingV4): number => {
  let serviceCharge = 0;

  if (Array.isArray(booking.preorders)) {
    serviceCharge = getActivePreOrders(booking).reduce((total, order) => {
      total += order.service_charge_amount ?? 0;

      return total;
    }, 0);
  }

  return serviceCharge;
};

const calculateTotalPaid = (booking: BookingV4) => {
  const orders = booking.preorders ?? [];
  const deposits = booking.deposits ?? [];
  const refunds = booking.refunds ?? [];

  const paymentRefs = [
    ...new Set(
      orders.map((preOrder) => {
        return preOrder.payment_ref;
      })
    )
  ];

  // Get the total paid for orders when they were placed
  const ordersPaidTotal = paymentRefs.reduce((total, paymentRef) => {
    const deposit = deposits.find(
      (d) => d.payment_ref === paymentRef && d.type === "customer_preorder"
    );

    total += parseFloat(`${deposit?.amount ?? 0}`);

    deposit?.refunds?.forEach((refundRef) => {
      const refund = refunds.find((refund) => refund.refund_ref === refundRef);

      total -= refund ? parseFloat(`${refund?.amount ?? 0}`) : 0;
    });

    return total;
  }, 0);

  // Get the total of any manually added deposits that were for pre-orders
  const depositsTotal = deposits
    .filter(
      (deposit) =>
        deposit.payment_for === "preorders" && deposit.status === "paid"
    )
    .reduce((carry, deposit) => {
      carry += parseFloat(`${deposit?.amount ?? 0}`);

      // Subtract the amount of any refunds associated with this deposit
      deposit.refunds?.forEach((refundRef) => {
        const refund = refunds.find(
          (refund) => refund.refund_ref === refundRef
        );

        carry -= refund ? parseFloat(`${refund?.amount ?? 0}`) : 0;
      });

      return carry;
    }, 0);

  let depositToReduce = depositsTotal;

  orders.every((order) => {
    // If this wasn't paid when completing and we still have deposits for orders
    if (!order.payment_ref && depositToReduce > 0) {
      // the amount to pay is the total cost or the amount remaining
      const paidAmount =
        order.total_cost > depositToReduce ? depositToReduce : order.total_cost;

      depositToReduce -= paidAmount;

      if (depositToReduce === 0) {
        return false;
      }
    }

    return true;
  });

  return { ordersPaidTotal, depositsTotal };
};

const getBookingMinSpend = (booking: BookingV4): number => {
  let minSpend = 0;

  if (booking.min_spend?.amount && booking.min_spend.per_head) {
    minSpend = booking.min_spend.amount * booking.num_people;
  } else if (booking.min_spend?.amount) {
    minSpend = parseFloat(`${booking.min_spend.amount}`);
  }

  return minSpend;
};

const getBookingRemainingSpend = (booking: BookingV4): number => {
  let remainingSpend = 0;
  const minSpend = getBookingMinSpend(booking);

  if (minSpend) {
    const packageTotal = getPreOrderTotalCost(booking);
    const paidTowardsMin = booking.min_spend?.paid_amount
      ? parseFloat(`${booking.min_spend.paid_amount}`)
      : 0;
    remainingSpend = Math.max(minSpend - packageTotal - paidTowardsMin, 0);
  }

  return remainingSpend;
};

const toCurrency = (value: number | undefined, currency: string): string =>
  value?.toLocaleString(window.locale, {
    style: "currency",
    currency
  }) ?? "";

const getSubType = (subType?: string): string => {
  switch (subType) {
    case "canape":
      return "Canapé";

    case "bowl":
      return "Bowl Food";

    case "amuse_bouche":
      return "Amuse Bouche";

    case "substantial_canapes":
      return "Substantial Canapes";

    case "starter":
      return "Starter";

    case "palate_cleanser":
      return "Palate Cleanser";

    case "first_course":
      return "First Course";

    case "second_course":
      return "Second Course";

    case "third_course":
      return "Third Course";

    case "fourth_course":
      return "Fourth Course";

    case "fifth_course":
      return "Fifth Course";

    case "main":
      return "Main";

    case "side":
      return "Side Dish";

    case "dessert":
      return "Dessert";

    case "dessert_canape":
      return "Dessert Canapé";

    case "cheese":
      return "Cheese course";

    case "brunch":
      return "Brunch";

    case "snack":
      return "Snack";

    case "tapas":
      return "Tapas";

    case "food_station":
      return "Food Station";

    default: {
      const text = subType?.split("_");
      const capitalizedText = text?.map((t) => {
        if (!t) return "";
        return t[0].toUpperCase() + t.slice(1);
      });
      return capitalizedText?.join(" ") ?? "";
    }
  }
};
